import BottomSheet from '@gorhom/bottom-sheet';
import moment from 'moment-timezone';
import { useUtxEarnSubscriptionsGetSubscriptionDetail } from '@utx/utx-client';
import { useMemo, useRef, useState } from 'react';

import { ProgressStatus } from '@/design-system/components/progress';
import { formatDate } from '@/utils/dateFormatting';
import { AutoRenewalProps } from '../../components/autoRenewal/AutoRenewal.web';
import { RedeemAssetModalProps } from '../redemption/RedeemAsset.web';
import { SubscriptionBodyProps } from './SubscriptionBody';
import { useTranslation } from 'react-i18next';

interface UseSubscriptionPreviewParams {
  id: string;
  onUpdate: () => void;
}

export const useSubscriptionDetails = (params: UseSubscriptionPreviewParams) => {
  const { id, onUpdate } = params;
  const { t } = useTranslation('earn');

  const query = useUtxEarnSubscriptionsGetSubscriptionDetail(id, {
    query: {
      refetchOnMount: false,
      refetchInterval: 0,
      retry: 0
    }
  });

  const statusData = useMemo(() => {
    if (!query.data?.data) return [];
    const subscription = query.data.data;
    let result: Array<ProgressStatus> = [
      {
        name: t('earn-invest::glossary.subscription'),
        status: 'active',
        date: formatDate(subscription.subscriptionDate),
        noFormat: true
      },
      {
        name: t('earn-invest::glossary.first-reward'),
        status: moment(subscription.firstRewardAccrualDate).isBefore(moment()) ? 'active' : 'inactive',
        date: formatDate(subscription.firstRewardAccrualDate || ''),
        noFormat: true
      }
    ];

    if (
      subscription.nextRewardDate &&
      subscription.nextRewardDate !== null &&
      subscription.nextRewardDate !== subscription.firstRewardAccrualDate
    ) {
      result = [
        ...result,
        {
          name: t('earn-invest::glossary.next-reward'),
          status: 'inactive',
          date: formatDate(subscription.nextRewardDate || ''),
          noFormat: true
        }
      ];
    }

    if (subscription.maturityDate) {
      result = [
        ...result,
        {
          name: t('earn-invest::glossary.maturity'),
          status: moment(subscription.maturityDate).isBefore(moment()) ? 'active' : 'inactive',
          date: formatDate(subscription.maturityDate),
          noFormat: true
        }
      ];
    }

    return result;
  }, [query.data?.data]);

  const subscription = query.data?.data;

  const subscriptionBodyProps: SubscriptionBodyProps = {
    statusData,
    subscription
  };

  return {
    query,
    isLoading: query.isLoading || query.isFetching,
    subscriptionDetails: query.data?.data,
    subscriptionBodyProps
  };
};
